import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AppLayout from '../components/AppLayout';
import BranchingQuestionnaireForm from '../components/BranchingQuestionnaireForm';
import toast from 'react-hot-toast';
import { generateGroupTeachingPlan } from '../lib/functions';

const GroupQuestionnaireFlow = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessmentType, setAssessmentType] = useState(null); // 'individual' | 'group-level'
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [studentAssessments, setStudentAssessments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchGroup();
  }, [groupId]);

  const fetchGroup = async () => {
    if (!groupId) return;
    
    setLoading(true);
    try {
      const groupSnap = await getDoc(doc(db, 'groupTeachingPlans', groupId));
      if (groupSnap.exists()) {
        setGroup({ id: groupSnap.id, ...groupSnap.data() });
      } else {
        toast.error('Group not found');
        navigate('/teaching-plans');
      }
    } catch (error) {
      console.error('Error fetching group:', error);
      toast.error('Failed to load group');
    } finally {
      setLoading(false);
    }
  };

  const handleAssessmentTypeChoice = (type) => {
    setAssessmentType(type);
    if (type === 'individual') {
      setStudentAssessments([]);
      setCurrentStudentIndex(0);
    }
  };

  const handleIndividualAssessmentComplete = (questionnaireData) => {
    const currentStudent = group.students[currentStudentIndex];
    const newAssessment = {
      studentId: currentStudent.id,
      studentName: `${currentStudent.firstName} ${currentStudent.lastName}`,
      questionnaireAnswers: questionnaireData
    };

    const updatedAssessments = [...studentAssessments, newAssessment];
    setStudentAssessments(updatedAssessments);

    if (currentStudentIndex < group.students.length - 1) {
      // Move to next student
      setCurrentStudentIndex(currentStudentIndex + 1);
      toast.success(`Assessment complete for ${currentStudent.firstName}`);
    } else {
      // All assessments complete
      handleGenerateGroupPlan(updatedAssessments, 'individual');
    }
  };

  const handleGroupLevelAssessmentComplete = (questionnaireData) => {
    // Apply same profile to all students
    const assessments = group.students.map(student => ({
      studentId: student.id,
      studentName: `${student.firstName} ${student.lastName}`,
      questionnaireAnswers: questionnaireData
    }));
    handleGenerateGroupPlan(assessments, 'group-level');
  };

  const handleGenerateGroupPlan = async (assessments, type) => {
    setSubmitting(true);
    try {
      await generateGroupTeachingPlan({
        groupId,
        studentIds: group.studentIds,
        assessments,
        assessmentType: type,
        termId: 'current',
      });
      toast.success('Group teaching plan generated successfully!');
      navigate(`/teaching-plans/group/${groupId}`);
    } catch (error) {
      console.error('Error generating group teaching plan:', error);
      toast.error('Failed to generate group teaching plan. Creating placeholder.');
      try {
        await updateDoc(doc(db, 'groupTeachingPlans', groupId), {
          status: 'active',
          assessments,
          assessmentType: type,
          lastUpdated: new Date().toISOString(),
        });
        navigate(`/teaching-plans/group/${groupId}`);
      } catch (updateError) {
        console.error('Error updating group:', updateError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      </AppLayout>
    );
  }

  if (!group) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Group not found</h2>
          <button
            onClick={() => navigate('/teaching-plans')}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            Back to Teaching Plans
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{group.groupName} - Assessment</h1>
          <p className="text-gray-600 mt-1">
            {group.students.length} students in this group
          </p>
        </div>

        {!assessmentType && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Assessment Method</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                onClick={() => handleAssessmentTypeChoice('individual')}
                className="p-6 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all text-left group"
              >
                <div className="flex items-start gap-3 mb-3">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-900">
                      Individual Assessments
                    </h3>
                    <p className="text-sm text-gray-600 mt-2">
                      Complete the NC routing questionnaire for each student separately. 
                      More precise but takes longer.
                    </p>
                    <div className="mt-3 text-sm font-medium text-indigo-600">
                      {group.students.length} assessments required →
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleAssessmentTypeChoice('group-level')}
                className="p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
              >
                <div className="flex items-start gap-3 mb-3">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-green-900">
                      Group-Level Assessment
                    </h3>
                    <p className="text-sm text-gray-600 mt-2">
                      Complete one assessment representing the group's general abilities. 
                      Faster but less precise for mixed-ability groups.
                    </p>
                    <div className="mt-3 text-sm font-medium text-green-600">
                      1 assessment only →
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {assessmentType === 'individual' && (
          <div className="space-y-6">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-indigo-900">
                    Assessing Student {currentStudentIndex + 1} of {group.students.length}
                  </h3>
                  <p className="text-sm text-indigo-700 mt-1">
                    {group.students[currentStudentIndex]?.firstName} {group.students[currentStudentIndex]?.lastName}
                  </p>
                </div>
                <div className="text-sm text-indigo-700">
                  {studentAssessments.length} / {group.students.length} completed
                </div>
              </div>
              <div className="mt-3">
                <div className="w-full bg-indigo-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(studentAssessments.length / group.students.length) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <BranchingQuestionnaireForm
              onSubmit={handleIndividualAssessmentComplete}
              onBack={() => setAssessmentType(null)}
            />
          </div>
        )}

        {assessmentType === 'group-level' && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-900">
                Group-Level Assessment
              </h3>
              <p className="text-sm text-green-700 mt-1">
                Answer based on the group's general ability level. This profile will be applied to all {group.students.length} students.
              </p>
            </div>

            <BranchingQuestionnaireForm
              onSubmit={handleGroupLevelAssessmentComplete}
              onBack={() => setAssessmentType(null)}
            />
          </div>
        )}

        {submitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3"></div>
                <span className="text-gray-700">Generating group teaching plan...</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default GroupQuestionnaireFlow;
